import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import { URL_SHORTEN } from '../config/environment';
import axios from 'axios';

/**
 * Bulk Upload Service
 * Handles all bulk upload-related API calls
 */

export const bulkUploadService = {
  /**
   * Download bulk upload template
   * @returns {Promise<Blob>} Template file blob
   */
  downloadTemplate: async () => {
    const response = await axios.get(`${URL_SHORTEN}/api/bulk-upload/template`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Upload bulk URLs
   * @param {FormData} formData - Bulk upload file data
   * @returns {Promise<Object>} Upload response
   */
  uploadBulkUrls: async (formData, signal = undefined) => {
    const response = await axios.post(`${URL_SHORTEN}/api/bulk-upload/upload`, formData, {
      headers: { 
        Authorization: `Bearer ${tokenManager.getAccessToken()}`,
        'Content-Type': 'multipart/form-data'
      },
      signal
    });
    return response.data;
  },

  /**
   * Import bulk URLs directly from SFTP
   * @param {Object} payload - { accountId, sftpConfigId, remoteFilePath }
   * @returns {Promise<Object>} Import response
   */
importFromSftp: async ({ accountId, sftpConfigId, remoteFilePath }) => {
  const response = await axios.post(
    `${USER_URL}/api/account-configs/${accountId}/sftp-config/import`,
    {
      sftpConfigId,
      remoteFilePath,
      importType: 'CSV'
    },
    {
      headers: {
        Authorization: `Bearer ${tokenManager.getAccessToken()}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
},

  /**
   * Get bulk upload history
   * @param {string} accountId - Account ID
   * @param {string} fromDate - Start date (optional, format: YYYY-MM-DD)
   * @param {string} toDate - End date (optional, format: YYYY-MM-DD)
   * @param {number} page - Page number (0-based, default: 0)
   * @param {number} size - Page size (default: 5)
   * @returns {Promise<Object|Array>} Bulk upload history (paged or array)
   */
  getBulkUploadHistory: async (accountId, fromDate = null, toDate = null, page = 0, size = 5, searchTerm = '') => {
    const params = { page, size, searchTerm: searchTerm || '' };
    
    if (fromDate && fromDate !== '') {
      params.fromDate = fromDate;
    }
    if (toDate && toDate !== '') {
      params.toDate = toDate;
    }

    const response = await axios.get(`${USER_URL}/api/bulk-url-shortening/account/${accountId}`, {
      params,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    const data = response.data;
    
    // Normalize new API envelope: { success, message, data: { totalItems, totalPages, pageSize, currentPage, items: [] } }
    if (data && data.success && data.data && Array.isArray(data.data.items)) {
      const payload = data.data;
      return {
        content: payload.items,
        totalPages: payload.totalPages ?? 0,
        totalElements: payload.totalItems ?? payload.items.length ?? 0,
        pageSize: payload.pageSize ?? size,
        currentPage: payload.currentPage ?? page
      };
    }
    
    // Fallback to previous shapes (paged or raw array)
    return data;
  },

  /**
   * Download bulk upload result
   * @param {string} resultFileName - Result file name
   * @returns {Promise<Blob>} Result file blob
   */
  downloadResult: async (resultFileName) => {
    const response = await axios.get(`${URL_SHORTEN}/api/bulk-upload/download/result/${resultFileName}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Delete bulk upload item
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} Delete response
   */
  deleteBulkUploadItem: async (itemId) => {
    const response = await axios.delete(`${USER_URL}/api/bulk-url-shortening/${itemId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  }
};

export default bulkUploadService;
